import axios, { AxiosRequestConfig } from 'axios';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';
import IResponse from '../interfaces/IResponse';

const BASE_URL = `${baseURL}api/employees`;

export interface IEmployee {
  _id: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface IGetAllEmployeesResponse extends IResponse {
  employees: IEmployee[];
}

export type GetAllEmployeesParams = {
  isActive?: boolean;
};

const getAllEmployees = async (params?: GetAllEmployeesParams): Promise<IGetAllEmployeesResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
      params,
    };

    const res = await axios.get(BASE_URL, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Pobrano pracowników',
      employees: res.data?.employees || [],
    };
  } catch (error) {
    let message = 'Nie udało się pobrać pracowników';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, employees: [] };
  }
};

interface ICreateEmployeeResponse extends IResponse {
  employee: IEmployee | null;
}

const createEmployee = async (payload: {
  firstName: string;
  lastName: string;
  isActive?: boolean;
}): Promise<ICreateEmployeeResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.post(BASE_URL, payload, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Dodano pracownika',
      employee: res.data?.employee || null,
    };
  } catch (error) {
    let message = 'Nie udało się dodać pracownika';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, employee: null };
  }
};

interface IUpdateEmployeeResponse extends IResponse {
  employee: IEmployee | null;
}

const updateEmployee = async (
  employeeId: string,
  payload: { firstName?: string; lastName?: string; isActive?: boolean }
): Promise<IUpdateEmployeeResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.put(`${BASE_URL}/${employeeId}`, payload, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Zaktualizowano pracownika',
      employee: res.data?.employee || null,
    };
  } catch (error) {
    let message = 'Nie udało się zaktualizować pracownika';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, employee: null };
  }
};

interface IDeleteEmployeeResponse extends IResponse {
  employee: IEmployee | null;
}

const deleteEmployee = async (employeeId: string): Promise<IDeleteEmployeeResponse> => {
  try {
    const authConfig: AxiosRequestConfig = {
      headers: ApiHelper.createHeader(),
    };

    const res = await axios.delete(`${BASE_URL}/${employeeId}`, authConfig);
    return {
      ok: true,
      status: res.status,
      message: res.data?.message || 'Usunięto pracownika',
      employee: res.data?.employee || null,
    };
  } catch (error) {
    let message = 'Nie udało się usunąć pracownika';
    let status = 400;
    if (axios.isAxiosError(error) && error.response) {
      message = error.response.data?.message || message;
      status = error.response.status;
    }
    return { ok: false, status, message, employee: null };
  }
};

const employeesController = {
  getAllEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
};

export default employeesController;
